/**
 * Shared DOMPurify config for post + custom-page body HTML.
 *
 * The rich-text editor lets admins drop YouTube embeds (rendered as
 * <iframe>) and styled images. We allow those + their attributes, plus a
 * strict list of allowed iframe hosts so an attacker can't get a stored XSS
 * by saving an iframe pointing at a malicious origin.
 */

const ALLOWED_IFRAME_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "youtu.be",
  "dailymotion.com",
  "www.dailymotion.com",
  "open.spotify.com",
  "spotify.com",
]);

/**
 * Hook that runs after every iframe is parsed by DOMPurify. Strips any
 * iframe whose `src` host isn't on the allowlist so admins can only embed
 * YouTube/Dailymotion — not random scripty domains.
 */
export const sanitizeIframeHook = (node) => {
  if (node.tagName !== "IFRAME") return;
  let src = node.getAttribute("src") || "";

  // Dynamic watch-to-embed URL resolution directly inside DOMPurify's iframe hook
  // to retroactively fix any previously saved post bodies containing watch links!
  if (src && !src.includes("/embed/")) {
    const match = src.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube-nocookie\.com\/embed\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/i);
    if (match) {
      src = `https://www.youtube-nocookie.com/embed/${match[1]}`;
      node.setAttribute("src", src);
    } else {
      const dmMatch = src.match(/(?:dailymotion\.com\/(?:video|hub)\/|dai\.ly\/)([a-zA-Z0-9]+)/i);
      if (dmMatch) {
        src = `https://www.dailymotion.com/embed/video/${dmMatch[1]}`;
        node.setAttribute("src", src);
      }
    }
  }

  try {
    const url = new URL(src);
    if (!ALLOWED_IFRAME_HOSTS.has(url.hostname.toLowerCase())) {
      node.parentNode?.removeChild(node);
      return;
    }
  } catch {
    // Malformed URL — drop the node.
    node.parentNode?.removeChild(node);
    return;
  }
  // Always-on safety attributes for embedded videos.
  node.setAttribute("loading", "lazy");
  node.setAttribute("referrerpolicy", "no-referrer-when-downgrade");
  if (!node.getAttribute("allow")) {
    node.setAttribute(
      "allow",
      "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
    );
  }
  node.setAttribute("allowfullscreen", "true");
};

// Target values we'll let through on <a>. Anything else (or missing)
// falls back to the default link behaviour (same tab).
const SAFE_LINK_TARGETS = new Set(["_blank", "_self", "_parent", "_top"]);

// Protocols (and root/hash starts) we treat as intentional. Anything
// else that looks domain-shaped gets auto-prefixed with `https://`.
const HREF_HAS_PROTOCOL = /^(?:https?:|mailto:|tel:|ftp:|\/|#)/i;
// Cheap "looks like a domain" sniff: labels separated by dots, ending
// in a 2+ letter TLD, optionally followed by /path?query#hash.
// Catches www.example.co.uk, sub.brand.io/shop?x=1, etc. Deliberately
// excludes bare filenames (no dot) and IPs (rare in blog posts).
const HREF_LOOKS_LIKE_DOMAIN =
  /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?:[:/?#].*)?$/i;

/**
 * Hook that preserves `target` and `rel` on anchor tags. DOMPurify's
 * default policy strips these attributes regardless of `ADD_ATTR`, which
 * is why admin-chosen "open in new tab" links were ending up without a
 * target and silently opening in the same window. We whitelist them here
 * and clamp `target` to a known-safe set so we can't be tricked into
 * `target="javascript:..."` style trickery.
 */
export const sanitizeAnchorAttrHook = (node, data) => {
  // Self-hosted <video> + <source> attributes. DOMPurify's built-in
  // attribute-validity table strips `controls`, `playsinline`, `preload`
  // etc. on <video> even when they're explicitly in ADD_ATTR (same root
  // cause as the anchor `target` bug). Force-keep them so the native
  // browser player UI (with the play button) actually shows up on the
  // published page.
  if (node.tagName === "VIDEO" || node.tagName === "SOURCE") {
    const VIDEO_FORCE_KEEP = new Set([
      "controls",
      "playsinline",
      "preload",
      "poster",
      "muted",
      "loop",
      "autoplay",
      "type",
      "src",
    ]);
    if (VIDEO_FORCE_KEEP.has(data.attrName)) {
      data.forceKeepAttr = true;
    }
    return;
  }

  if (node.tagName !== "A") return;
  if (data.attrName === "href") {
    // Auto-prefix protocol-less external links so admins pasting
    // `www.ccfcprints.co.uk` don't end up with a relative link that
    // resolves as `/post/123/www.ccfcprints.co.uk` on the live site.
    const raw = (data.attrValue || "").trim();
    if (raw && !HREF_HAS_PROTOCOL.test(raw) && HREF_LOOKS_LIKE_DOMAIN.test(raw)) {
      data.attrValue = `https://${raw}`;
    }
    return;
  }
  if (data.attrName === "target") {
    if (SAFE_LINK_TARGETS.has(data.attrValue)) {
      // `forceKeepAttr` (not `keepAttr`) bypasses DOMPurify's built-in
      // attribute-validity table, which strips `target` on <a> tags
      // regardless of ADD_ATTR.
      data.forceKeepAttr = true;
    } else {
      data.keepAttr = false;
    }
  } else if (node.tagName === "A" && data.attrName === "rel") {
    data.forceKeepAttr = true;
  }
};

/**
 * Hook that runs on every element. When a `<span data-text-size="N">` is
 * encountered, walk up to the nearest `<p>` ancestor and tag it with
 * `data-has-small-text="N"` so plain attribute-selector CSS can tighten
 * the paragraph's line-height + margins without relying on `:has()`
 * (which behaves inconsistently on older mobile browsers when applied
 * to dangerouslySetInnerHTML content).
 *
 * Only fires for the four "small" presets — anything 16pt+ keeps the
 * default prose leading.
 */
const SMALL_TEXT_SIZES = new Set(["6", "8", "10", "12"]);
export const sanitizeSmallTextSizeHook = (node) => {
  if (node.tagName !== "SPAN") return;
  const size = node.getAttribute("data-text-size");
  if (!size || !SMALL_TEXT_SIZES.has(size)) return;
  let parent = node.parentNode;
  while (parent && parent.tagName !== "P") {
    parent = parent.parentNode;
    // Stop if we walk out of the tree we're sanitising.
    if (!parent || parent === document) return;
  }
  if (parent && parent.tagName === "P") {
    const existing = parent.getAttribute("data-has-small-text");
    // Keep the SMALLEST size encountered so the tightest leading wins
    // when one paragraph mixes (e.g.) 6pt + 12pt — readability tradeoff.
    if (!existing || Number(size) < Number(existing)) {
      parent.setAttribute("data-has-small-text", size);
    }
  }
};

/**
 * Post-process a sanitized HTML string: parse it, walk it, and stamp
 * `data-has-small-text="N"` on every `<p>` that contains a
 * `<span data-text-size="N">` (with N in {6,8,10,12}).
 *
 * This is the robust workaround for cross-browser `:has()` inconsistency
 * — once the attribute is on the <p> itself, CSS just needs a plain
 * attribute selector (`p[data-has-small-text]`) which has worked
 * everywhere since IE7.
 *
 * Returns the modified HTML string; falls back to the input unchanged
 * on any parsing failure (defensive — the SSR build doesn't have
 * DOMParser).
 */
export const flagSmallTextParagraphs = (html) => {
  if (!html || typeof DOMParser === "undefined") return html;
  try {
    const doc = new DOMParser().parseFromString(
      `<!doctype html><body>${html}`,
      "text/html",
    );
    const spans = doc.querySelectorAll("span[data-text-size]");
    let touched = false;
    spans.forEach((span) => {
      const size = span.getAttribute("data-text-size");
      if (!SMALL_TEXT_SIZES.has(size)) return;
      const para = span.closest("p");
      if (!para) return;
      const existing = para.getAttribute("data-has-small-text");
      if (!existing || Number(size) < Number(existing)) {
        para.setAttribute("data-has-small-text", size);
        touched = true;
      }
    });
    // Wrap any `<img data-caption="…">` in a <figure> with a
    // <figcaption> so the credit text renders directly underneath
    // the image (inside any float wrapper). We transfer the img's
    // alignment + width classes / styles up to the figure so the
    // float/centring + width still apply to the whole figure block,
    // and reset the img to fill its parent figure 100%. This keeps
    // existing alignment + resizing behaviour intact for both
    // captioned and un-captioned images.
    const captioned = doc.querySelectorAll("img[data-caption]");
    captioned.forEach((img) => {
      const caption = (img.getAttribute("data-caption") || "").trim();
      if (!caption) return;
      // If the img sits inside an <a> (image link), we treat the anchor
      // as the "movable unit" so the link survives the figure wrap.
      const anchor =
        img.parentElement && img.parentElement.tagName === "A"
          ? img.parentElement
          : null;
      const movable = anchor || img;
      // Skip if already wrapped (idempotency for double-sanitise).
      if (movable.parentElement && movable.parentElement.tagName === "FIGURE"
          && movable.parentElement.classList.contains("image-figure")) return;
      const fig = doc.createElement("figure");
      fig.className = `image-figure ${img.className || ""}`.trim();
      // Move alignment + width attributes/styles up so floats wrap the
      // caption together with the image.
      const align = img.getAttribute("data-alignment");
      if (align) fig.setAttribute("data-alignment", align);
      const widthAttr = img.getAttribute("data-width");
      if (widthAttr) fig.setAttribute("data-width", widthAttr);
      const inlineStyle = img.getAttribute("style") || "";
      const widthMatch = inlineStyle.match(/width:\s*([^;]+)/i);
      if (widthMatch) fig.style.width = widthMatch[1].trim();
      // Also carry the aspect-ratio hint over to the figure (and keep it
      // on the img too) — without it, a captioned image has NO size hint
      // until the browser fetches it, so the figure starts at zero/auto
      // height and pops open once the image loads. That's a real layout
      // shift, more visible on slower mobile connections than on
      // desktop, which is what actually made this "look inconsistent
      // between mobile and desktop" rather than any real crop/squash.
      const ratioMatch = inlineStyle.match(/aspect-ratio:\s*([^;]+)/i);
      if (ratioMatch) fig.style.aspectRatio = ratioMatch[1].trim();
      // Strip the float / width / mx-auto classes off the inner img
      // (they're now on the figure) and let the img fill the figure —
      // but keep the aspect-ratio hint on the img itself too.
      img.className = "image-figure-img";
      img.removeAttribute("style");
      if (ratioMatch) img.style.aspectRatio = ratioMatch[1].trim();
      movable.parentNode.insertBefore(fig, movable);
      fig.appendChild(movable);
      const cap = doc.createElement("figcaption");
      cap.className = "image-figure-caption";
      cap.textContent = caption;
      fig.appendChild(cap);
      touched = true;
    });
    // Wrap any saved table in a `.tableWrapper` div (ProseMirror only adds
    // that wrapper live inside the editor — published/saved HTML has the
    // bare `<table>`). Without a separate wrapper box, the mobile CSS rule
    // that puts `overflow-x: auto` on the table *itself* can't actually
    // shrink the table's own outer box — the resizable-table extension
    // saves an inline `min-width` (e.g. 645px, from the colgroup) that
    // beats our `max-width: 100%`, since an element's own `overflow`
    // property only scrolls content overflowing an already-constrained
    // box, it can't constrain the box's own size. A wrapper div lets the
    // *wrapper* be clamped to 100% of the article width while the table
    // keeps its natural/colgroup width and scrolls inside that box — the
    // standard responsive-table pattern.
    const bareTables = doc.querySelectorAll("table.tiptap-table");
    bareTables.forEach((table) => {
      if (
        table.parentElement &&
        table.parentElement.classList.contains("tableWrapper")
      ) {
        return;
      }
      const wrapper = doc.createElement("div");
      wrapper.className = "tableWrapper";
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
      touched = true;
    });
    return touched ? doc.body.innerHTML : html;
  } catch {
    return html;
  }
};

export const POST_HTML_PURIFY_CONFIG = {
  ADD_TAGS: ["iframe", "video", "source"],
  ADD_ATTR: [
    "data-alignment",
    "data-width",
    "data-rounded",
    "data-bordered",
    "data-text-size",
    "data-new-badge",
    "target",
    "rel",
    "style",
    "allow",
    "allowfullscreen",
    "frameborder",
    "loading",
    "referrerpolicy",
    "controls",
    "playsinline",
    "preload",
    "poster",
    "muted",
    "type",
    // TipTap's table extension persists column widths via `colwidth` (a
    // semicolon-separated px list) and uses native colspan/rowspan. Keep
    // them so saved tables render back exactly as the admin laid them out.
    "colspan",
    "rowspan",
    "colwidth",
    // Set by `sanitizeSmallTextSizeHook` so CSS can target the parent
    // <p> of a small data-text-size span without `:has()`.
    "data-has-small-text",
    // Set by the rich editor on images that have a caption / photo
    // credit. `wrapImageCaptions` reads this at render time and wraps
    // the img in a <figure> with a <figcaption>.
    "data-caption",
    // Set by the rich editor when the admin makes an image clickable —
    // the editor also wraps the <img> in an <a href> at render time.
    // We keep the data-attribute too so re-parse survives even if the
    // outer anchor is ever stripped.
    "data-href",
    "data-href-target",
    // Real intrinsic pixel size captured at insert time (see
    // getImageDimensions() in RichTextEditor.jsx) — powers the
    // aspect-ratio CSS on the <img> (already covered by the `style`
    // allowlist entry above) and lets re-editing a post rebuild the
    // TipTap node's attrs correctly. Data-* pass through by default too
    // (see note below) but listed explicitly as a safeguard.
    "data-natural-width",
    "data-natural-height",
    // Set on the `imageTextBlock` node's wrapper div (float-left/right
    // replacement) to say which grid column the image sits in. Data-*
    // pass through DOMPurify by default, listed as the same safeguard
    // convention as the other data-* attrs here.
    "data-side",
    // Set on the `imageTextBlock` node's `.tiptap-image-text-media` div
    // to say where the image sits *within* its own cell (left/centre/
    // right — independent of `data-side` above, which picks the
    // column). Same Data-* safeguard convention as the rest of this list.
    "data-align",
    // Marks an embedded Fan Poll placeholder div (see PollNode in
    // RichTextEditor.jsx / POLL_RE in RichBodyWithCarousels.jsx). Data-*
    // attributes pass through DOMPurify by default (ALLOW_DATA_ATTR),
    // but list it explicitly as a safeguard against that default ever
    // changing and silently breaking embedded polls.
    "data-poll-id",
    // Set by the HtmlEmbed node on `<div class="html-embed-placeholder">`.
    // The public post-page's `hydrateHtmlEmbeds` decodes this base64 blob
    // and injects the real third-party embed markup at mount time.
    "data-html-b64",
    "data-hydrated",
    // Some contenteditable-serialized placeholders round-trip with this
    // attribute set. Keeping it stops React from complaining and also
    // makes the placeholder non-editable in any downstream ProseMirror
    // instance that re-parses the saved HTML.
    "contenteditable",
  ],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|\/|#)/i,
};
